// src/modules/notifications/pages/NotificationSettings.jsx
//
// The category × channel matrix.
//
// Per-category rather than per-type on purpose: a row per notification type
// would be a screen nobody can navigate, and every product that has tried it
// (GitHub, Linear, Slack, Jira) has converged on categories.
//
// The server decides what is toggleable — each cell arrives with `enabled`,
// `mutable` and `implemented` already resolved, so this page renders rules it
// does not have to know. That is why a lock can be tightened server-side
// without a frontend release.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Typography, Switch, Table, Space, Button, Alert, Skeleton, Tag,
  Tooltip, message as antdMessage,
} from 'antd';
import { LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { fetchNotificationPreferences, saveNotificationPreferences } from '../api';
import { CHANNEL_LABEL } from '../registry';

const { Title, Text, Paragraph } = Typography;

// Column order. Unimplemented channels still render, greyed, so the shape of
// the eventual product is visible without pretending it delivers.
const CHANNELS = ['inApp', 'push', 'email'];

const NotificationSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchNotificationPreferences());
    } catch (err) {
      antdMessage.error('Could not load notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const typesByCategory = useMemo(() => {
    const map = {};
    (data?.types || []).forEach((t) => {
      if (!map[t.category]) map[t.category] = [];
      map[t.category].push(t);
    });
    return map;
  }, [data]);

  const toggle = async (category, channel, value) => {
    // Optimistic: the switch must feel instant, and a failed save is reverted
    // by the reload in the catch.
    setData((prev) => ({
      ...prev,
      categories: prev.categories.map((c) => (c.category === category
        ? { ...c, channels: { ...c.channels, [channel]: { ...c.channels[channel], enabled: value } } }
        : c)),
    }));

    setSaving(true);
    try {
      const fresh = await saveNotificationPreferences({ [category]: { [channel]: value } });
      setData((prev) => ({ ...prev, categories: fresh.categories }));
    } catch (err) {
      antdMessage.error('Could not save that change');
      load();
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Category',
      dataIndex: 'label',
      key: 'label',
      render: (label, row) => (
        <Space direction="vertical" size={2}>
          <Text strong>{label}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {(typesByCategory[row.category] || []).map((t) => t.label).join(' · ') || 'No notification types yet'}
          </Text>
        </Space>
      ),
    },
    ...CHANNELS.map((channel) => ({
      title: CHANNEL_LABEL[channel],
      key: channel,
      width: 130,
      align: 'center',
      render: (_, row) => {
        const cell = row.channels?.[channel] || {};
        if (!cell.implemented) {
          return <Tag bordered={false} style={{ fontSize: 11, opacity: 0.7 }}>Coming soon</Tag>;
        }
        if (!cell.mutable) {
          return (
            <Tooltip title="This alert cannot be switched off. It reports the system failing — muting it would hide the only warning you get.">
              <Space size={4}>
                <LockOutlined style={{ opacity: 0.5 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Always on</Text>
              </Space>
            </Tooltip>
          );
        }
        return (
          <Switch
            size="small"
            checked={!!cell.enabled}
            disabled={saving}
            onChange={(v) => toggle(row.category, channel, v)}
          />
        );
      },
    })),
  ];

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Notification settings</Title>
          <Text type="secondary">Choose what reaches you, and how.</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Everything is on by default"
        description={(
          <Paragraph style={{ margin: 0 }}>
            Turn off anything you do not want. Security and operations alerts stay on:
            they report the system failing rather than routine work, and a missed one
            can mean an approved payment never actually executed.
          </Paragraph>
        )}
      />

      <Card styles={{ body: { padding: 0 } }}>
        {loading ? (
          <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 6 }} /></div>
        ) : (
          <Table
            rowKey="category"
            dataSource={data?.categories || []}
            columns={columns}
            pagination={false}
            size="middle"
            scroll={{ x: 640 }}
          />
        )}
      </Card>

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        In-app notifications appear in the bell. Push notifications reach your browser
        or phone when the CRM is closed, and need notification permission in that browser.
      </Text>
    </div>
  );
};

export default NotificationSettings;
